import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { AppSetting } from '../../_models/admin/app-setting.models';
import { AppSettingsService } from '../../_services/app-settings/app-settings.service';
import { DynamicConfirmModalComponent } from '../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';
import { AppSettingHistoryData, AppSettingHistoryDrawerComponent } from './app-setting-history-drawer.component';
import { SETTING_BRANDS, SETTING_CATEGORY_ORDER } from './app-settings.constants';
import { AdminEmailComponent } from '../../shared/admin-ux/admin-email.component';

export interface SettingGroup {
  category: string;
  settings: AppSetting[];
}

/**
 * Settings store editor: values an admin can change at runtime without a deploy, grouped by area. Each setting
 * is edited by its type (number with min/max, on/off, text), checked here before it is sent — the API checks
 * again — and confirmed before it is saved. A setting with per-brand values is badged "Per brand — review for
 * collapse" (LEAP V2 is brand-agnostic, so per-brand values are the exception): each brand's value can be
 * edited, and "Collapse to one value" deletes them so every brand uses the global value. Lives on the
 * Settings tab of System Controls.
 */
@Component({
  selector: 'app-settings-editor',
  standalone: true,
  imports: [DatePipe, AdminEmailComponent],
  templateUrl: 'app-settings-editor.component.html'
})
export class AppSettingsEditorComponent implements OnInit {

  public readonly brands = SETTING_BRANDS;
  public settings: AppSetting[] = [];
  public groups: SettingGroup[] = [];
  /** The unsaved value per setting key, as a string (bool is "true"/"false"). */
  public pending: { [settingKey: string]: string } = {};
  /** The unsaved value per brand override, keyed by brandKey(). */
  public pendingBrand: { [key: string]: string } = {};
  public loading = false;
  /** The setting key (or brandKey) being written. */
  public saving: string = null;
  public error: string = null;

  constructor(private service: AppSettingsService, private dialog: MatDialog) { }

  ngOnInit(): void {
    this.load();
  }

  public load(): void {
    this.loading = true;
    this.error = null;
    this.service.getSettings().subscribe({
      next: settings => {
        this.loading = false;
        this.setSettings(settings || []);
        this.pending = {};
        this.pendingBrand = {};
      },
      error: error => {
        this.loading = false;
        this.error = (error && error.message) || 'Could not load the settings.';
      }
    });
  }

  public current(setting: AppSetting): string {
    const value = this.pending[setting.settingKey];
    return value !== undefined ? value : (setting.value ?? '');
  }

  public edit(setting: AppSetting, value: string): void {
    this.pending = { ...this.pending, [setting.settingKey]: value ?? '' };
  }

  public isBool(setting: AppSetting): boolean {
    return (setting.valueType || '').toLowerCase() === 'bool';
  }

  public isInt(setting: AppSetting): boolean {
    return (setting.valueType || '').toLowerCase() === 'int';
  }

  public isDirty(setting: AppSetting): boolean {
    const value = this.pending[setting.settingKey];
    return value !== undefined && value !== (setting.value ?? '');
  }

  /** Why the unsaved value cannot be saved, or null when it can. Mirrors the API's 422 rules. */
  public validate(setting: AppSetting): string | null {
    return this.validateValue(setting, this.current(setting));
  }

  public canSave(setting: AppSetting): boolean {
    return this.isDirty(setting) && !this.validate(setting) && this.saving !== setting.settingKey;
  }

  /** Ask first — the value applies to every user — then save and show the setting the server returns. */
  public save(setting: AppSetting): void {
    if (!this.canSave(setting)) {
      return;
    }
    const value = this.isBool(setting) ? this.current(setting) : this.current(setting).trim();
    const who = this.isPerBrand(setting) ? 'every brand without its own value' : 'every user';
    const message = 'Set "' + setting.displayName + '" to ' + this.shown(setting, value) + '? This applies to ' + who + '.';

    this.confirm(message, 'Save', () => this.write(setting.settingKey, setting.displayName, this.service.saveSetting(setting.settingKey, value),
      () => {
        const { [setting.settingKey]: _, ...rest } = this.pending;
        this.pending = rest;
      }));
  }

  // ── Per-brand values ──────────────────────────────────────────────────────────────────────────

  public isPerBrand(setting: AppSetting): boolean {
    return (setting.brandOverrides || []).length > 0;
  }

  public brandKey(setting: AppSetting, companyId: number): string {
    return setting.settingKey + '|' + companyId;
  }

  /** The brand's stored override, or null when the brand uses the global value. */
  public brandValue(setting: AppSetting, companyId: number): string | null {
    const o = (setting.brandOverrides || []).find(x => x.companyId === companyId);
    return o ? (o.value ?? '') : null;
  }

  public currentBrand(setting: AppSetting, companyId: number): string {
    const value = this.pendingBrand[this.brandKey(setting, companyId)];
    return value !== undefined ? value : (this.brandValue(setting, companyId) ?? '');
  }

  public editBrand(setting: AppSetting, companyId: number, value: string): void {
    this.pendingBrand = { ...this.pendingBrand, [this.brandKey(setting, companyId)]: value ?? '' };
  }

  public isBrandDirty(setting: AppSetting, companyId: number): boolean {
    const value = this.pendingBrand[this.brandKey(setting, companyId)];
    return value !== undefined && value !== (this.brandValue(setting, companyId) ?? '');
  }

  public validateBrand(setting: AppSetting, companyId: number): string | null {
    return this.validateValue(setting, this.currentBrand(setting, companyId));
  }

  public canSaveBrand(setting: AppSetting, companyId: number): boolean {
    return this.isBrandDirty(setting, companyId) && !this.validateBrand(setting, companyId)
      && this.saving !== this.brandKey(setting, companyId);
  }

  public saveBrand(setting: AppSetting, companyId: number): void {
    if (!this.canSaveBrand(setting, companyId)) {
      return;
    }
    const key = this.brandKey(setting, companyId);
    const value = this.isBool(setting) ? this.currentBrand(setting, companyId) : this.currentBrand(setting, companyId).trim();
    const message = 'Set "' + setting.displayName + '" for ' + this.brandName(companyId) + ' to ' + this.shown(setting, value) + '?';

    this.confirm(message, 'Save', () => this.write(key, setting.displayName, this.service.saveBrandOverride(setting.settingKey, companyId, value),
      () => {
        const { [key]: _, ...rest } = this.pendingBrand;
        this.pendingBrand = rest;
      }));
  }

  /** Deletes every brand's value after a confirmation, so every brand uses the global value. */
  public collapse(setting: AppSetting): void {
    if (!this.isPerBrand(setting) || this.saving === setting.settingKey) {
      return;
    }
    const message = 'Collapse "' + setting.displayName + '" to one value? Every brand will use ' + this.shown(setting, setting.value ?? '')
      + ', and the per-brand values are deleted.';

    this.confirm(message, 'Collapse', () => this.write(setting.settingKey, setting.displayName, this.service.collapse(setting.settingKey),
      () => {
        this.pendingBrand = Object.fromEntries(Object.entries(this.pendingBrand).filter(([k]) => !k.startsWith(setting.settingKey + '|')));
      }));
  }

  public brandName(companyId: number): string {
    return (this.brands.find(b => b.companyId === companyId) || { name: 'brand ' + companyId }).name;
  }

  public history(setting: AppSetting): void {
    const data: AppSettingHistoryData = { setting };
    this.dialog.open(AppSettingHistoryDrawerComponent, {
      data,
      width: '520px',
      height: '100vh',
      maxWidth: '100vw',
      position: { right: '0', top: '0' }
    });
  }

  // ── Internals ─────────────────────────────────────────────────────────────────────────────────

  private validateValue(setting: AppSetting, raw: string): string | null {
    const value = (raw ?? '').trim();
    if (this.isInt(setting)) {
      if (!/^-?\d+$/.test(value)) {
        return 'Enter a whole number.';
      }
      const n = Number(value);
      if (setting.minValue != null && n < setting.minValue) {
        return 'The lowest allowed value is ' + setting.minValue + '.';
      }
      if (setting.maxValue != null && n > setting.maxValue) {
        return 'The highest allowed value is ' + setting.maxValue + '.';
      }
    } else if (this.isBool(setting) && value !== 'true' && value !== 'false') {
      return 'Choose on or off.';
    }
    return null;
  }

  private shown(setting: AppSetting, value: string): string {
    if (this.isBool(setting)) {
      return value === 'true' ? 'on' : 'off';
    }
    return value === '' ? 'blank' : '"' + value + '"';
  }

  private confirm(message: string, label: string, onConfirmed: () => void): void {
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: message, confirmLabel: label }
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          onConfirmed();
        }
      });
  }

  private write(savingKey: string, displayName: string, call: Observable<AppSetting>, onSaved: () => void): void {
    this.saving = savingKey;
    this.error = null;
    call.subscribe({
      next: updated => {
        this.saving = null;
        if (updated) {
          this.setSettings(this.settings.map(s => s.settingKey === updated.settingKey ? updated : s));
        }
        onSaved();
      },
      error: error => {
        this.saving = null;
        this.error = 'Could not save "' + displayName + '": ' + ((error && error.message) || 'please try again.');
      }
    });
  }

  private setSettings(settings: AppSetting[]): void {
    this.settings = settings;
    const byCategory = new Map<string, AppSetting[]>();
    for (const s of settings) {
      const category = s.category || 'Other';
      byCategory.set(category, [...(byCategory.get(category) || []), s]);
    }
    const known = SETTING_CATEGORY_ORDER.filter(c => byCategory.has(c));
    const others = [...byCategory.keys()].filter(c => !SETTING_CATEGORY_ORDER.includes(c)).sort((a, b) => a.localeCompare(b));
    this.groups = [...known, ...others].map(category => ({ category, settings: byCategory.get(category) }));
  }
}
